import React, { useState, useEffect, useCallback } from 'react';
import { FaExternalLinkAlt, FaPaperPlane, FaSpinner, FaArrowLeft } from 'react-icons/fa';
import { getEmailThread, updateDraft, allowSender } from '../../services/emailMonitorService';
import GiftVoucherPanel from './GiftVoucherPanel';

// One email's conversation, laid out like a chat: header, the messages scrolling, the reply box
// pinned at the bottom. That shape matters here — a support thread can run to a dozen messages, and
// the earlier design nested it in a fixed-height box inside a table row, which meant scrolling a box
// inside a scrolling page to read it.
//
// The thread is read live from the mailbox rather than from Firestore, so it includes replies sent
// straight from Outlook that Loope never saw. Bodies arrive as plain text (the API asks Graph for
// text, not HTML), so they can be rendered as-is without sanitising.

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
};

const EmailThreadPanel = ({ email, onSend, sending, onBack, onChanged, notify }) => {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threadError, setThreadError] = useState(null);

  // Seeded from the AI draft when there is one; in this phase there usually is not, and the admin writes it.
  const [replyText, setReplyText] = useState(email.draftText || '');
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState(null);
  // Knowledge entries this draft was answered from. Removing the citation keeps the
  // words on screen but marks the draft as hand-written from then on.
  const [citedIds, setCitedIds] = useState(
    Array.isArray(email.draftFaqIds) ? email.draftFaqIds : []
  );

  const alreadySent = email.status === 'sent';

  // Guessed senders are grouped with their guessed side but never verified: this
  // sender is not in Loope. One tap watches them from now on (future mail only).
  const guess = email.senderCategory === 'unknown' && (email.senderGuess?.group === 'franchisee' || email.senderGuess?.group === 'supplier')
    ? email.senderGuess.group
    : null;
  const [allowing, setAllowing] = useState(false);

  const monitorThisSender = async () => {
    if (!email.from || allowing) return;
    setAllowing(true);
    try {
      const res = await allowSender(email.from);
      setSaveNote({ type: 'success', text: res?.message || `Now monitoring ${email.from}.` });
    } catch (e) {
      setSaveNote({ type: 'error', text: e.message || 'Could not monitor this sender.' });
    } finally {
      setAllowing(false);
    }
  };

  const loadThread = useCallback(async () => {
    setLoading(true);
    setThreadError(null);
    try {
      setThread(await getEmailThread(email.id));
    } catch (e) {
      setThreadError(e.message || 'Could not load this conversation.');
    } finally {
      setLoading(false);
    }
  }, [email.id]);

  useEffect(() => { loadThread(); }, [loadThread]);

  // Switching emails must not carry the previous one's half-written reply across.
  useEffect(() => {
    setReplyText(email.draftText || '');
    setSaveNote(null);
    setCitedIds(Array.isArray(email.draftFaqIds) ? email.draftFaqIds : []);
  }, [email.id, email.draftText, email.draftFaqIds]);

  // The reply text lives on the record, so saving is what makes "send" send the words on screen.
  const persist = async () => {
    setSaving(true);
    setSaveNote(null);
    try {
      await updateDraft(email.id, replyText);
      setSaveNote({ type: 'success', text: 'Saved.' });
      return true;
    } catch (e) {
      setSaveNote({ type: 'error', text: e.message || 'Could not save the reply.' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!replyText.trim()) return;
    if (await persist()) onSend(email, replyText);
  };

  const removeCitation = async () => {
    setSaving(true);
    try {
      await updateDraft(email.id, replyText, []);
      setCitedIds([]);
      setSaveNote({ type: 'success', text: 'Citation removed. The words stay; the draft is now hand-written.' });
    } catch (e) {
      setSaveNote({ type: 'error', text: e.message || 'Could not remove the citation.' });
    } finally {
      setSaving(false);
    }
  };

  const messages = thread?.messages || [];

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to the list"
            className="md:hidden p-2 -ml-2 text-gray-500 hover:text-gray-800 rounded shrink-0"
          >
            <FaArrowLeft />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-900 truncate">{email.subject || '(no subject)'}</h2>
            <p className="text-sm text-gray-500 truncate">
              {email.senderName || email.from}
              {email.senderName ? ` · ${email.from}` : ''}
              {email.mailbox ? ` → ${email.mailbox}` : ''}
            </p>
          </div>
          {email.webLink && (
            <a
              href={email.webLink}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-xs text-jims-cyan hover:text-jims-cyan-dark flex items-center gap-1 mt-1"
            >
              Outlook <FaExternalLinkAlt />
            </a>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 min-h-0">
        {guess && (
          <div className="flex items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2">
            <span className="text-xs text-amber-800">
              Not in Loope — grouped as a likely {guess}. Allow-list if valid.
            </span>
            <button
              type="button"
              onClick={monitorThisSender}
              disabled={allowing || saving || sending}
              className="text-xs font-medium text-amber-800 underline hover:text-amber-900 disabled:opacity-50 shrink-0"
            >
              {allowing ? 'Adding…' : 'Monitor this sender'}
            </button>
          </div>
        )}
        {thread?.threadUnavailable && (
          <p className="text-xs text-amber-700">
            The rest of the conversation could not be read from the mailbox, so only this email is shown.
          </p>
        )}
        {threadError && <p className="text-xs text-red-700">{threadError}</p>}

        {loading ? (
          <div className="text-sm text-gray-500">Loading conversation&hellip;</div>
        ) : messages.length === 0 ? (
          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded p-3 whitespace-pre-wrap">
            {email.bodyPreview || '(no preview available)'}
          </div>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={`bg-white border rounded p-3 ${m.isMonitored ? 'border-jims-cyan ring-1 ring-jims-cyan/30' : 'border-gray-200'}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                <span className="text-sm font-medium text-gray-900">{m.fromName || m.from || '(unknown sender)'}</span>
                <span className="text-xs text-gray-500">{formatDate(m.receivedDateTime)}</span>
              </div>
              {m.from && m.fromName && <div className="text-xs text-gray-500 mb-1">{m.from}</div>}
              <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">{m.body || '(empty message)'}</div>
            </div>
          ))
        )}
      </div>

      <div className="border-t border-gray-200 bg-white px-4 py-3">
        {email.giftVoucher ? (
          <GiftVoucherPanel email={email} onChanged={onChanged} notify={notify} />
        ) : alreadySent ? (
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-700">
              Replied{email.sentBy ? ` by ${email.sentBy}` : ''}{email.sentAt ? ` on ${formatDate(email.sentAt)}` : ''}
            </p>
            <div className="mt-1 whitespace-pre-wrap text-gray-700 max-h-32 overflow-y-auto">
              {email.draftText || '(no copy of the reply was kept)'}
            </div>
          </div>
        ) : (
          <>
            {citedIds.length > 0 && (
              <div className="mb-2 flex items-center justify-between gap-2 rounded border border-green-200 bg-green-50 px-3 py-2">
                <span className="text-xs text-green-800">
                  Draft answered from the knowledge base ({citedIds.length} {citedIds.length === 1 ? 'entry' : 'entries'} cited).
                </span>
                <button
                  type="button"
                  onClick={removeCitation}
                  disabled={saving || sending}
                  className="text-xs font-medium text-green-800 underline hover:text-green-900 disabled:opacity-50 shrink-0"
                >
                  Remove citation
                </button>
              </div>
            )}
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={4}
              className="w-full text-sm border border-gray-300 rounded p-3 focus:ring-jims-cyan focus:border-jims-cyan"
              placeholder={`Write your reply to ${email.senderName || email.from}…`}
            />
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleSend}
                disabled={saving || sending || !replyText.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />} Send reply
              </button>
              <button
                type="button"
                onClick={persist}
                disabled={saving || sending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save without sending'}
              </button>
              <span className="text-xs text-gray-500">Goes out from {email.mailbox}</span>
              {saveNote && (
                <span className={`text-xs ${saveNote.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>{saveNote.text}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailThreadPanel;
